import _ from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { hideMenu } from 'react-contextmenu';
import { KEY_RECOIL, TYPE_RHYTHM_EVENT_ENUM } from '@/shared/config';
import { useEventsInfoBeatHr } from '@/entities/beat';

import { BULK_ACTION_ENUM, LOAD_BEAT_STATUS } from '@/shared/model';
import { buildGroupEventFilterSnapshot, updateGroupEvents } from '@/entities/event';
import { cancelAllAPI } from '@/shared/lib/apiHelpers';
import {
  selectActiveTab,
  selectEventChanges,
  selectEventFilter,
  selectGroupEventChanges,
  selectIsActiveTab,
  useReportStore,
} from '@/entities/report';

const MENU_ID = 'BEATHR';

/**
 * Backs the beats tab left column — the event type tab list, its context menu and the per-type change indicators. Switching tabs hides the menu, cancels in-flight API calls and clears paging, selection, jump and bulk action state; memoizes which event types carry group or unapplied changes, applies context menu actions against the current event filter snapshot, and resets the bulk actions as the tab gains or loses focus.
 *
 * @returns An object with the active event type tab, the pending event and group event changes, the per-type event counts, the tab click and left column scroll handlers, the group-change and unapplied-change lookup maps, and the context menu item handler.
 */
const useLeftColumn = () => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setActiveButtonAction = useReportStore((s) => s.actions.setActiveTab);
  /**
   * Writes the active event type tab of the beats tab left column into the report store. Forwards the value to the shared setActiveTab action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setActiveButton = (v: Parameters<typeof setActiveButtonAction>[1]) =>
    setActiveButtonAction(keyRecoil, v);
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Writes the beats tab empty-data flag into the report store. Forwards the value to the shared setIsEmptyData action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Writes the selected date value of the beats tab into the report store. Forwards the value to the shared setSelectedDateValue action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const setSelectedColumnInfoAction = useReportStore((s) => s.actions.setSelectedColumnInfo);
  /**
   * Writes the selected column info of the beats tab event navigator into the report store. Forwards the value to the shared setSelectedColumnInfo action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedColumnInfo = (v: Parameters<typeof setSelectedColumnInfoAction>[1]) =>
    setSelectedColumnInfoAction(keyRecoil, v);
  const setSelectedEventInfoAction = useReportStore((s) => s.actions.setSelectedEventInfo);
  /**
   * Writes the selected event info of the beats tab event navigator into the report store. Forwards the value to the shared setSelectedEventInfo action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedEventInfo = (v: Parameters<typeof setSelectedEventInfoAction>[1]) =>
    setSelectedEventInfoAction(keyRecoil, v);
  const setPrevColumnInfoAction = useReportStore((s) => s.actions.setPrevColumnInfo);
  /**
   * Writes the previous column info of the beats tab event navigator into the report store. Forwards the value to the shared setPrevColumnInfo action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setPrevColumnInfo = (v: Parameters<typeof setPrevColumnInfoAction>[1]) =>
    setPrevColumnInfoAction(keyRecoil, v);
  const setNextColumnInfoAction = useReportStore((s) => s.actions.setNextColumnInfo);
  /**
   * Writes the next column info of the beats tab event navigator into the report store. Forwards the value to the shared setNextColumnInfo action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setNextColumnInfo = (v: Parameters<typeof setNextColumnInfoAction>[1]) =>
    setNextColumnInfoAction(keyRecoil, v);
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Writes the beats tab multiple strip selection into the report store. Forwards the value to the shared setSelectedMultipleStrip action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the beats tab strip page index into the report store. Forwards the value to the shared setPageIndex action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const resetControlModalAction = useReportStore((s) => s.actions.resetControlModal);
  /**
   * Closes every beats tab modal by resetting that tab's control modal flags. Calls the shared resetControlModal action with this tab's key.
   *
   * @returns Nothing.
   */
  const resetControlModal = () => resetControlModalAction(keyRecoil);
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the currently selected beats tab strip into the report store. Forwards the value to the shared setSelectedStrip action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const setTotalStrip = useReportStore((s) => s.actions.setTotalStrip);
  const setListSelectedCells = useReportStore((s) => s.actions.setListSelectedCells);
  const setLoadingBeatStatus = useReportStore((s) => s.actions.setLoadingBeatStatus);
  const setEcgObjectAction = useReportStore((s) => s.actions.setEcgObject);
  /**
   * Writes the beats tab ECG viewer object into the report store. Forwards the value to the shared setEcgObject action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEcgObject = (v: Parameters<typeof setEcgObjectAction>[1]) =>
    setEcgObjectAction(keyRecoil, v);
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Writes the beats tab strip loading flag into the report store. Forwards the value to the shared setIsLoadingStrip action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Writes the beats tab jump-to date into the report store. Forwards the value to the shared setJumpDate action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Writes the beats tab jump-to time into the report store. Forwards the value to the shared setJumpTime action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const setBulkAction = useReportStore((s) => s.actions.setBulkAction);
  const setBulkActionByCell = useReportStore((s) => s.actions.setBulkActionByCell);
  const isActiveTab = useReportStore(selectIsActiveTab(keyRecoil));
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Writes the beats tab jump-to portion index into the report store. Forwards the value to the shared setJumpPortion action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const eventChanges = useReportStore(selectEventChanges(keyRecoil));
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the beats tab pending event changes into the report store. Forwards the value to the shared setEventChanges action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  const setActionSnapshot = useReportStore((s) => s.actions.setActionSnapshot);
  const eventsInfo = useEventsInfoBeatHr();
  const groupEventChanges = useReportStore(selectGroupEventChanges(keyRecoil));
  const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
  /**
   * Writes the beats tab group event changes into the report store. Forwards the value to the shared setGroupEventChanges action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setGroupEventChanges = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
    setGroupEventChangesAction(keyRecoil, v);
  const specialEventTypes = useReportStore((s) => s.specialEventTypes);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the beats tab event options snapshot that undo/redo replays into the report store. Forwards the value to the shared setEventOptions action bound to this tab's key.
   *
   * @param v - Next value, or an updater function receiving the previous one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  const setSelectedTemplate = useReportStore((s) => s.actions.setSelectedTemplate);

  /**
   * Switches the beats tab left column to another event type and clears everything scoped to the previous one. Hides the context menu, returns early when the tab is unchanged, cancels in-flight API calls, then resets the control modal, paging, date and jump values, the column, event and strip selections, selected cells, ECG object, beat loading status, both bulk actions, the snapshot counter and the selected template, forcing the loading flag on for the artifact tab.
   *
   * @param tab - Event type tab to activate.
   * @returns Nothing.
   */
  const onClickTab = (tab: any) => {
    hideMenu({
      id: MENU_ID,
    });
    if (activeButton === tab) {
      return;
    }
    cancelAllAPI();
    // resetEcgViewerConfigStore(););
    resetControlModal();
    setPageIndex((prev) => (prev.index === 0 ? prev : { index: 1 }));
    setSelectedDateValue(null);
    setActiveButton(tab);
    setIsEmptyData(false);
    setSelectedColumnInfo([]);
    setSelectedEventInfo([]);
    setPrevColumnInfo([]);
    setNextColumnInfo([]);
    setSelectedStrip(null);
    setSelectedMultipleStrip([]);
    setJumpDate(undefined);
    setJumpTime(undefined);
    setJumpPortion(0);
    setTotalStrip(0);
    setIsLoadingStrip((prev: any) => {
      if (tab === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
        return true;
      }
      return prev;
    });
    setListSelectedCells((prev: any) => (prev.length > 0 ? [] : prev));
    setEcgObject({});
    setLoadingBeatStatus((prev: any) =>
      prev.status !== LOAD_BEAT_STATUS.NONE ? { status: LOAD_BEAT_STATUS.NONE } : prev,
    );
    setBulkAction(keyRecoil, BULK_ACTION_ENUM.NOTHING);
    setBulkActionByCell(keyRecoil, BULK_ACTION_ENUM.NOTHING);
    setActionSnapshot(1);
    setSelectedTemplate(keyRecoil, {});
  };

  /**
   * Dismisses the open event type context menu when the beats tab left column is scrolled. Hides the menu registered under the left column menu id.
   *
   * @returns Nothing.
   */
  const handleScrollLeftColumn = () => {
    hideMenu({
      id: MENU_ID,
    });
  };

  const hasGroupTypeChangeTabInfos: any = useMemo(() => {
    const result: any = {};
    _.forEach(groupEventChanges, (groupEvent: any) => {
      if (!result[groupEvent.groupEventType]) {
        result[groupEvent.groupEventType] = true;
      }
    });
    return result;
  }, [groupEventChanges]);

  const hasUnappliedChangeTabInfos: any = useMemo(() => {
    const result: any = {};
    _.forEach(eventChanges, (event: any) => {
      if (!result[event.oldType]) {
        result[event.oldType] = true;
      }
    });
    return result;
  }, [eventChanges]);

  /**
   * Applies a group action chosen from the left column event type context menu. Skips the markedAllReviewed action, otherwise builds a filter snapshot from the current event filter and the special event types, recomputes the group event changes for the menu's event type and action, re-commits the existing event changes through the event options so undo/redo records the step, and closes the control modal.
   *
   * @param e - Context menu click event.
   * @param data - Menu item payload carrying the target eventType and the action to apply.
   * @returns Nothing.
   */
  const onItemMenuClick = useCallback(
    (e: any, data: any) => {
      const { eventType, action } = data;
      if (action !== 'markedAllReviewed') {
        const filterSnapshot = buildGroupEventFilterSnapshot(eventFilter, specialEventTypes);
        const updatedGroupEvents = updateGroupEvents(
          eventType,
          action,
          groupEventChanges,
          filterSnapshot,
        );
        setGroupEventChanges(updatedGroupEvents);
        setEventOptions((prevOptions: any) => ({
          ...prevOptions,
          eventChangesState: eventChanges,
        }));
        setEventChanges(eventChanges);
        resetControlModal();
      }
    },
    [eventChanges, eventFilter, groupEventChanges, specialEventTypes],
  );

  useEffect(() => {
    if (isActiveTab) {
      setTimeout(() => {
        setActionSnapshot(1);
      }, 200);
    } else {
      setBulkAction(keyRecoil, BULK_ACTION_ENUM.NOTHING);
      setBulkActionByCell(keyRecoil, BULK_ACTION_ENUM.NOTHING);
    }
    setSelectedMultipleStrip([]);
  }, [isActiveTab]);

  return {
    activeButton,
    eventChanges,
    groupEventChanges,
    eventsInfo,
    onClickTab,
    handleScrollLeftColumn,
    hasGroupTypeChangeTabInfos,
    hasUnappliedChangeTabInfos,
    onItemMenuClick,
  };
};

export default useLeftColumn;
